interface Envelope<T> {
  data: T | null;
  meta: Record<string, unknown>;
  error: { code: string; message: string; details?: unknown } | null;
}

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Register a callback invoked when an *authenticated* request is rejected with 401
 * (token expired or revoked server-side). The AuthProvider wires this to a global
 * logout so a dead session bounces the user to login instead of failing silently.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  token?: string | null,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (token) headers.set('authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE}/api/v1${path}`, { ...init, headers });
  const bodyText = await res.text();
  const body = bodyText ? (JSON.parse(bodyText) as Envelope<T>) : ({} as Envelope<T>);

  if (!res.ok || body.error) {
    const err = body.error ?? { code: 'HTTP_ERROR', message: res.statusText };
    // A rejected token on an authenticated request means the session is dead. Trigger a
    // global logout. We gate on `token` so a failed login/register (no token sent) doesn't.
    if (res.status === 401 && token) unauthorizedHandler?.();
    throw new ApiError(err.code, err.message, res.status, err.details);
  }
  return body.data as T;
}

export const api = {
  get: <T>(path: string, token?: string | null) => request<T>(path, {}, token),
  post: <T>(path: string, body: unknown, token?: string | null) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }, token),
  patch: <T>(path: string, body: unknown, token?: string | null) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, token),
  delete: (path: string, token?: string | null) =>
    request<void>(path, { method: 'DELETE' }, token),
};
